import { Button, Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap';
import { Controller, useForm } from 'react-hook-form';
import { enqueueSnackbar } from 'notistack';
import { zodResolver } from '@hookform/resolvers/zod';
import { ColorRing } from 'react-loader-spinner';
import { useTranslation } from 'react-i18next';

import { useAddImageToClientStore } from './useAddImageToClientStore';

import { AddImageToClientValues, addImageToClientSchema } from 'api/clients/types';
import { MLInput } from 'components/Input/Input';
import useEditImageToClient from 'hooks/Clients/useEditImageToClient';

interface Props {
  refetch: () => void;
  isAddModal?: boolean;
}

export const AddImageToClientModal: React.FC<Props> = ({ refetch, isAddModal = true }) => {
  const { clientId, isOpen, onAddImageToClientClose } = useAddImageToClientStore();
  const { editImageToClient, isLoading } = useEditImageToClient();
  const { t } = useTranslation();
  const { handleSubmit, control, reset } = useForm<AddImageToClientValues>({
    resolver: zodResolver(addImageToClientSchema),
  });

  const onReset = () => {
    reset();
  };

  const onSubmitEdit = (data: AddImageToClientValues) => {
    editImageToClient(
      { clientId, info: data },
      {
        onSuccess: () => {
          refetch();
          onAddImageToClientClose();
          onReset();
          enqueueSnackbar(t('ClientsAccordion.accordion.modal-edit-img.success-message'), { variant: 'success' });
        },
      },
    );
  };
  return (
    <Modal isOpen={isOpen} toggle={onAddImageToClientClose}>
      <ModalHeader
        className="border-bottom py-3"
        toggle={() => {
          onAddImageToClientClose();
          onReset();
          refetch();
        }}
      >
        <div className="d-flex justify-content-between align-items-center w-100 gap-5">
          <div className="text-purple">
            {isAddModal
              ? t('ClientsAccordion.accordion.modal-add-img.title')
              : t('ClientsAccordion.accordion.modal-edit-img.title')}
          </div>
        </div>
      </ModalHeader>
      <ModalBody>
        {isLoading ? (
          <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 120 }}>
            <ColorRing
              visible
              height={80}
              width={80}
              ariaLabel="color-ring-loading"
              wrapperStyle={{}}
              wrapperClass="color-ring-wrapper"
              colors={['#9C7AE2', '#E27A8C', '#C0E27A', '#7AE2D0', '#F7F18F']}
            />
          </div>
        ) : (
          <div className="d-flex justify-content-center">
            <form className="w-100">
              <div className="row">
                <div className="mb-2 col-md-12 col-sm-12">
                  <Controller
                    control={control}
                    name="image"
                    render={({ field, fieldState: { error } }) => (
                      <MLInput
                        label={t('ClientsAccordion.accordion.modal-add-img.label')}
                        type="file"
                        accept="image/*"
                        onChange={e => {
                          const file = e.target.files?.[0] || null;
                          field.onChange(file);
                        }}
                        error={error?.message}
                      />
                    )}
                  />
                </div>
              </div>
              <div className="row">
                <p className="mb-1 col-md-12 col-sm-12 text-muted">
                  {t('ClientsAccordion.accordion.modal-add-img.help-text')}
                </p>
              </div>
            </form>
          </div>
        )}
      </ModalBody>
      <ModalFooter>
        <Button
          className="btn btn-ghost-danger"
          onClick={() => {
            onAddImageToClientClose();
            onReset();
          }}
        >
          {t('ClientsAccordion.accordion.modal-add-img.close-btn')}
        </Button>
        <Button className="btn btn-ghost-success" onClick={handleSubmit(onSubmitEdit)}>
          {isAddModal
            ? t('ClientsAccordion.accordion.modal-add-img.save-btn')
            : t('ClientsAccordion.accordion.modal-edit-img.save-btn')}
        </Button>
      </ModalFooter>
    </Modal>
  );
};
